import {
	DialogCancellableOpenResult,
	DialogCloseResult,
	DialogOpenPromise,
	DialogService,
} from 'aurelia-dialog';
import { autoinject } from 'aurelia-framework';
import { I18N } from 'aurelia-i18n';

import { AcurityTable } from '~/components/acurity-table/acurity-table';
import {
	ClassicPanelDialog,
	ClassicPanelDialogConstructor,
	ClassicPanelDialogModel,
} from '~/components/classic-panel/classic-panel-dialog';
import { FetchUtils } from '~/utils/fetch-utils';

/**
 * See https://aurelia.io/docs/plugins/dialog
 *
 */
@autoinject
export class ClassicPanelDialogUtils {
	constructor(
		private readonly dialogService: DialogService,
		private readonly i18n: I18N,
	) {}

	/**
	 * Show a simple classic panel dialog that has keyboard shortcuts disabled.
	 * Do not want escape/enter shortcuts to interfere with navigating form.
	 *
	 * @see showCustomDialog
	 * @param {any} dialogModel The model used in the dialog component
	 * @param {AcurityTable} recordSelectorTable The table displaying the records
	 * @returns {Promise<void>}
	 */
	public showClassicPanelDialog = (
		dialogModel: ClassicPanelDialogModel,
		recordSelectorTable?: AcurityTable,
	): Promise<void> => {
		return this.showCustomClassicPanelDialog(
			ClassicPanelDialog,
			dialogModel,
			recordSelectorTable,
		);
	};

	/**
	 * Show a custom classic panel dialog that has keyboard shortcuts disabled
	 * and needs to takes a dialog component and model as parameters.
	 * Used for dialogs with special elements outside their customisable areas.
	 * This method automatically handles the closed value and so returns a void Promise
	 * Do not want escape/enter shortcuts to interfere with navigating form.
	 *
	 * @see showCustomDialog
	 * @param {object} dialogObject The dialog component to be used
	 * @param {any} dialogModel The model used in the dialog component
	 * @param {AcurityTable} recordSelectorTable The table displaying the records
	 * @returns {Promise<void>}
	 */
	public showCustomClassicPanelDialog = <
		DialogModel extends ClassicPanelDialogModel,
	>(
		dialogObject:
			| ClassicPanelDialogConstructor<DialogModel>
			| ClassicPanelDialog<DialogModel>,
		dialogModel: DialogModel,
		recordSelectorTable?: AcurityTable,
	): Promise<void> => {
		return this.dialogService
			.open({
				viewModel: dialogObject,
				model: dialogModel,
				lock: true,
				keyboard: false,
			})
			.whenClosed((value) => {
				this.handleClosedValue(value, recordSelectorTable);
			})
			.catch(FetchUtils.logAndDisplayError);
	};

	/**
	 * Handles the closed value that is returned from simple or custom classic panel dialog.
	 * @param {DialogCloseResult} value The result returned from the classic panel dialog
	 * @param {AcurityTable} recordSelectorTable The table displaying the records
	 */
	public handleClosedValue(
		value: DialogCloseResult,
		recordSelectorTable?: AcurityTable,
	) {
		if (
			!value.wasCancelled &&
			recordSelectorTable &&
			recordSelectorTable.isAjaxTable
		) {
			recordSelectorTable.refreshAjaxTable();
		}
	}

	/**
	 * Similar to showCustomClassicPanelDialog() but returns a DialogOpenPromise
	 * so we can intercept the closed value and do things with it eg access the
	 * classic panel's form model
	 *
	 * @param {object} dialogObject The dialog component to be used
	 * @param {any} dialogModel The model used in the dialog component
	 * @returns {DialogOpenPromise<DialogCancellableOpenResult>}
	 */
	public showCustomClassicPanelDialogReturnOpen = <
		DialogModel extends ClassicPanelDialogModel,
	>(
		dialogObject:
			| ClassicPanelDialogConstructor<DialogModel>
			| ClassicPanelDialog<DialogModel>,
		dialogModel: DialogModel,
	): DialogOpenPromise<DialogCancellableOpenResult> => {
		return this.dialogService.open({
			viewModel: dialogObject,
			model: dialogModel,
			lock: true,
			keyboard: false,
		});
	};

	public closeAll() {
		this.dialogService.closeAll();
	}
}
